import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ChatTranscript } from '@/features/votes/chat';
import type {
  ActionCardChat,
  ActionCardDetails,
  ActionCardWatch,
} from '@/features/votes/entity-votes';
import {
  AsSeenDetails,
  AutoReviewVerdict,
} from '@/features/votes/MeetingAssistDetailsCard';

import type { ReactNode } from 'react';

function formatDateTime(dateString: string) {
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function CardTimeline({ card }: { card: ActionCardDetails }) {
  const events = [
    { label: 'Made', at: card.created_at },
    { label: 'Revised', at: card.state.revised_at },
    { label: 'Resolved', at: card.state.resolved_at },
    { label: 'Dismissed', at: card.state.dismissed_at },
    { label: 'Pinned', at: card.pinned_at },
    { label: 'Snoozed until', at: card.snoozed_until },
    { label: 'Expires', at: card.expires_at },
  ].flatMap(({ label, at }) => (at ? [{ label, at }] : []));
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
      {events.map((event) => (
        <span key={event.label}>
          {event.label} {formatDateTime(event.at)}
        </span>
      ))}
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <h4 className="font-medium text-sm">{title}</h4>
      {children}
    </div>
  );
}

function ChatSection({
  title,
  chat,
  missing,
  highlightedMessageId,
}: {
  title: string;
  chat: ActionCardChat | null;
  missing: string;
  highlightedMessageId?: string | null;
}) {
  return (
    <Section title={title}>
      {chat ? (
        <details>
          <summary className="cursor-pointer text-sm text-muted-foreground">
            Show transcript
            <span className="ml-2 font-mono text-xs">{chat.chat_id}</span>
          </summary>
          <p className="mt-2 text-xs text-muted-foreground">
            Turns that ran after the vote are redacted
          </p>
          <div className="mt-2 rounded-md border p-4">
            <ChatTranscript
              chatHistory={chat.chat_history}
              highlightedMessageId={highlightedMessageId}
              highlightLabel="Made the card"
            />
          </div>
        </details>
      ) : (
        <p className="text-sm text-muted-foreground">{missing}</p>
      )}
    </Section>
  );
}

function WatchSummary({ watch }: { watch: ActionCardWatch }) {
  const lines = [
    watch.resolved && `Resolved the card: ${watch.resolved}`,
    watch.suggestion && `Suggests: ${watch.suggestion}`,
    watch.reason && `Reason: ${watch.reason}`,
    watch.note && `Note: ${watch.note}`,
    watch.kept && 'The user kept the card, so the watcher leaves it alone',
    watch.last_checked_at &&
      `Last finding ${formatDateTime(watch.last_checked_at)}`,
    watch.routine_id != null && `Watcher routine ${watch.routine_id}`,
  ].filter((line): line is string => !!line);
  return (
    <ul className="space-y-1 text-sm">
      {lines.map((line) => (
        <li key={line}>{line}</li>
      ))}
    </ul>
  );
}

export default function ActionCardDetailsCard({
  card,
}: {
  card: ActionCardDetails;
}) {
  const watcher = card.watcher;
  const runsBeforeVote = card.watcher_runs_before_vote;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Action Card</CardTitle>
        <CardDescription>
          #{card.local_id} · {card.state.title}
          <span className="ml-2 text-xs font-mono">{card.card_id}</span>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-3 rounded-md bg-muted/50 p-3 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline">{card.action_type}</Badge>
            <Badge variant="outline">From {card.origin}</Badge>
            <Badge variant="secondary">{card.state.status}</Badge>
            {card.importance !== null && (
              <Badge variant="outline">Importance {card.importance}/10</Badge>
            )}
            {card.state.agent_action && (
              <Badge variant="outline">{card.state.agent_action}</Badge>
            )}
            {card.state.original_content && (
              <Badge variant="outline">Differs from the agent's version</Badge>
            )}
            {card.changed_after_vote && (
              <Badge variant="destructive">Changed after the vote</Badge>
            )}
          </div>
          <CardTimeline card={card} />
          {card.context && (
            <div>
              <p className="text-xs text-muted-foreground">
                Why the agent made it
              </p>
              <p className="whitespace-pre-wrap">{card.context}</p>
            </div>
          )}
          <AsSeenDetails asSeen={card.state} />
          {card.auto_review && <AutoReviewVerdict review={card.auto_review} />}
        </div>

        {(card.parent_local_id !== null || card.children.length > 0) && (
          <Section title="Family">
            {card.parent_local_id !== null && (
              <p className="text-sm">Under card #{card.parent_local_id}</p>
            )}
            {card.children.length > 0 && (
              <ul className="space-y-1 text-sm">
                {card.children.map((child) => (
                  <li key={child.local_id}>
                    #{child.local_id} · {child.action_type}
                    {child.title && ` · ${child.title}`} · {child.status}
                  </li>
                ))}
              </ul>
            )}
          </Section>
        )}

        <ChatSection
          title={
            card.origin === 'routine'
              ? 'Routine run that made the card'
              : 'Chat that made the card'
          }
          chat={card.source_chat}
          missing="The card was not made in a chat"
          highlightedMessageId={card.assistant_message_id}
        />

        <ChatSection
          title="Chat about the card"
          chat={card.follow_up_chat}
          missing="The user did not open a chat from this card"
        />

        <Section title="Card watcher">
          {watcher ? <WatchSummary watch={watcher.watch} /> : null}
          {watcher?.run ? (
            <ChatSection
              title="Watcher run that last recorded a finding on the card"
              chat={watcher.run}
              missing=""
            />
          ) : (
            <p className="text-sm text-muted-foreground">
              {watcher
                ? 'No run recorded a finding on the card by the vote: the check before a run settled it from its integration, the finding predates run tracking, or it came after the vote'
                : 'No watcher run recorded a finding on this card'}
            </p>
          )}
          {runsBeforeVote.length > 0 ? (
            <>
              <p className="text-sm text-muted-foreground">
                {watcher?.run ? 'Other runs' : 'Runs'} that looked at the card
                before the vote, newest first:
              </p>
              {runsBeforeVote.map((run) => (
                <ChatSection
                  key={run.chat.chat_id}
                  title={`Watcher run · ${formatDateTime(run.ran_at)}`}
                  chat={run.chat}
                  missing=""
                />
              ))}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              No {watcher?.run ? 'other ' : ''}watcher run looked at this card
              before the vote
            </p>
          )}
        </Section>
      </CardContent>
    </Card>
  );
}
